import { DocumentGroup, UploadedDocumentInfoDocumentType } from '@urbanx/agx.campaigns.client';
import { DocumentTypesMap } from '@urbanx/agx-ui-components';
import {
  AgxCollapsible,
  Icon_LoaderOutline,
  AgxButton,
  AgxSpacer,
  AgxTypography,
  Icon_Chevron,
  Icon_DownloadOutline,
} from '@urbanx/agx.ui-components2';
import { useContext, useState } from 'react';
import { editFile, StaffGetUploadedFileLink } from 'services';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { splitPascalCase } from 'utils/stringUtils';
import './TasksFilesList.scss';

interface FilesListProps {
  files?: DocumentGroup[];
  id: string;
}

const openFilePicker = (): Promise<File | null> => {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = () => {
      const file = input.files?.[0] ?? null;
      resolve(file);
    };
    input.click();
  });
};

interface TaskDocumentItemProps {
  id: string;
  fullPath: string;
  documentId?: string;
  documentType?: UploadedDocumentInfoDocumentType | string | null;
  fileName?: string | null;
  title?: string | null;
  getAuthToken: () => Promise<string>;
  isOpen: boolean;
  onToggle: (isOpen: boolean) => void;
}

const RESPONSE_CONTENT_DISPOSITION_INLINE = '&response-content-disposition=inline';
const GOOGLE_VIEWER_BASE = 'https://docs.google.com/gview?embedded=true&url=';

const getTaskDocumentTitle = (
  title?: string | null,
  documentType?: UploadedDocumentInfoDocumentType | string | null,
  fileName?: string | null
) => {
  if (title) return title;

  const documentTypeTitle = documentType
    ? DocumentTypesMap[documentType as keyof typeof DocumentTypesMap]
    : undefined;

  return documentTypeTitle || splitPascalCase(documentType ?? '') || fileName || '';
};

const TaskDocumentItem = ({
  id,
  fullPath,
  documentId,
  documentType,
  fileName,
  title,
  getAuthToken,
  isOpen,
  onToggle,
}: TaskDocumentItemProps) => {
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>();

  const {
    data: fileUrl,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['file-link', fullPath],
    enabled: !!fullPath,
    queryFn: async () => StaffGetUploadedFileLink(fullPath, getAuthToken),
  });

  const normalizedUrl = fileUrl ? fileUrl + RESPONSE_CONTENT_DISPOSITION_INLINE : undefined;
  const googleViewerUrl = normalizedUrl
    ? GOOGLE_VIEWER_BASE + encodeURIComponent(normalizedUrl)
    : undefined;
  const documentTitle = getTaskDocumentTitle(title, documentType, fileName);
  const downloadButtonId = `download-${documentId ?? fileName ?? 'document'}`;

  return (
    <AgxCollapsible
      key={documentId ?? fullPath}
      className="tasks-files-list__document"
      customTitle={
        <div className="tasks-files-list__title">
          <Icon_Chevron direction={isOpen ? 'up' : 'down'} width={20} height={20} />
          <AgxTypography
            id="tasks-files-list-document-title"
            type="caption"
            size="medium"
            color="var(--text-primary)"
          >
            {documentTitle}
          </AgxTypography>
          <AgxButton
            id={downloadButtonId}
            variant="link"
            text=""
            disabled={!normalizedUrl}
            leftIcon={<Icon_DownloadOutline width={14} height={14} color="var(--text-action)" />}
            tooltip="Download document"
            ariaLabel="Download document"
            renderLink={(props) => (
              <a href={normalizedUrl} target="_blank" rel="noopener noreferrer" {...props} />
            )}
          />
        </div>
      }
      enableToggle
      showBorder
      iconPosition="none"
      variant="withoutDivider"
      headerPadding={{ left: 'spacing-xl', right: 'spacing-xl' }}
      isContentFit
      backgroundColor="white"
      maxTitleLegth={25}
      open={isOpen}
      onToggle={onToggle}
      actionContent={
        <AgxSpacer direction="vertical">
          <AgxButton
            id={'upload_btn'}
            variant="link"
            disabled={isUploading}
            text={isUploading ? 'Uploading…' : 'Upload new file'}
            onClick={async () => {
              const selectedFile = await openFilePicker();
              if (!selectedFile) return;

              const token = await getAuthToken();

              const formData = new FormData();
              formData.append('file', selectedFile);

              try {
                setIsUploading(true);
                setUploadError(undefined);

                await editFile(
                  token,
                  formData,
                  id,
                  documentType as UploadedDocumentInfoDocumentType,
                  fullPath
                );
              } catch (error) {
                setUploadError(
                  error instanceof Error ? error.message : 'The file could not be uploaded.'
                );
              } finally {
                setIsUploading(false);

                queryClient.invalidateQueries({
                  queryKey: ['file-link', fullPath],
                });
              }
            }}
          />
          {uploadError && <AgxSpacer role="alert">{uploadError}</AgxSpacer>}
        </AgxSpacer>
      }
      content={
        <div className="tasks-files-list__preview-content">
          {isLoading && <div>Loading file…</div>}
          {!isLoading && isError && (
            <div className="tasks-files-list__preview-error">Failed to load file.</div>
          )}
          {!isLoading && !isUploading && googleViewerUrl ? (
            <iframe src={googleViewerUrl} className="tasks-files-list__preview-frame" />
          ) : (
            <Icon_LoaderOutline />
          )}
        </div>
      }
    />
  );
};

const TasksFilesList = ({ files = [], id }: FilesListProps) => {
  const { getAuthToken } = useContext(AuthContext);
  const documents = files.flatMap((group) => group.documents ?? []);
  const getDocumentKey = (doc: (typeof documents)[number], index: number) =>
    [doc.documentId, doc.containerFilePath, doc.fileName, doc.documentType, index]
      .filter((keyPart) => keyPart !== undefined && keyPart !== null && keyPart !== '')
      .join(':');
  const firstDocumentKey = documents[0] ? getDocumentKey(documents[0], 0) : undefined;
  // Accordion behaviour: opening a document collapses the previously open one.
  const [openDocumentKey, setOpenDocumentKey] = useState<string | undefined>(
    documents.length === 1 ? firstDocumentKey : undefined
  );

  return (
    <AgxSpacer className="tasks-files-list" direction="vertical" gap="spacing-sm" fill>
      <AgxTypography
        id="tasks-files-list-heading"
        type="body_ui_strong"
        size="small"
        color="var(--text-primary)"
      >
        Files
      </AgxTypography>
      {documents.map((doc, index) => {
        const fullPath = doc.containerFilePath ?? '';
        const documentKey = getDocumentKey(doc, index);

        return (
          <TaskDocumentItem
            key={documentKey}
            id={id}
            fullPath={fullPath}
            documentId={doc.documentId}
            documentType={doc.documentType}
            fileName={doc.fileName}
            title={doc.title}
            getAuthToken={getAuthToken}
            isOpen={openDocumentKey === documentKey}
            onToggle={(isOpen) => setOpenDocumentKey(isOpen ? documentKey : undefined)}
          />
        );
      })}
    </AgxSpacer>
  );
};

export default TasksFilesList;
